import { SyntheticEvent, useEffect, useState } from 'react';
import { Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Link, useLocation, useParams } from 'react-router';

import { useAppDispatch } from 'app/hooks';
import FileUpload from 'components/files/FileUpload';
import FormattedMessage from 'components/formatters/FormattedMessage';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { getTextAreaHeight } from 'components/forms/TextAreaInput';
import { ModalDialog } from 'components/modal/Modal';
import { imageFileTypes } from 'features/files/fileSlice';
import { IUser } from 'features/user/userAPI';
import { patchUserAsync, userForgotAsync } from 'features/user/userSlice';
import { getColorForId } from 'helpers/colors';
import { getPattern } from 'helpers/regex';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import { useUser } from 'selectors/userSelectors';
import UserAvatar from './UserAvatar';

import './UserForm.scss';

type UserProfileFormProps = {
  author?: IUser;
  editing?: boolean;
};

export default function UserProfileForm(
  props: UserProfileFormProps, // & FormProps,
) {
  const { author } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { user, locale, userIsAdmin } = useUser();

  const [data, setData] = useState<IUser & { password?: string }>({
    ...(author || user),
  });
  const [file, setFile] = useState<File | undefined>(undefined); // for avatar image
  const [previewSrc, setPreviewSrc] = useState<string | null>(null); // state for storing previewImage
  const [editing, setEditing] = useState((!author && props.editing) || false);
  const [upgraded, setUpgraded] = useState<boolean>(false);
  enum profileTabs {
    profile = 'profile',
    notifications = 'notifications',
    language = 'language',
    login = 'login',
  }
  const { section } = useParams();
  const activeSection = Object.keys(profileTabs).includes(section ?? '')
    ? profileTabs[section as keyof typeof profileTabs]
    : profileTabs.profile;
  const [activeTab, setActiveTab] = useState<profileTabs>(activeSection);
  const color = getColorForId((author || user).id ?? 0);
  const [everyDay, setEveryDay] = useState<boolean>(false);
  const { settings } = useAppSettings();

  useEffect(() => {
    const userData = author || user;
    setData({
      ...userData,
    });
    setPreviewSrc(userData.avatar?.uri || null);
  }, [user, author]);

  // Handle URL hash for direct tab navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && Object.values(profileTabs).includes(hash as profileTabs)) {
      setActiveTab(hash as profileTabs);
      if (hash !== profileTabs.profile) {
        setEditing(true);
      }
    }
  }, [location.hash]);

  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  useEffect(() => {
    doFormMsg(NoFormMsg, setFormMsg);
    // if (!show) setPreviewSrc(null);
  }, []);

  useEffect(() => {
    const ta = document.getElementById('About');
    if (ta) {
      ta.style.height =
        getTextAreaHeight(ta as HTMLTextAreaElement, 5, 10, editing)[0] + 'px';
    }
  }, [data, editing]);

  function handleChangeData(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setData({
      ...data,
      [target.name]: target.value,
    });
  }

  async function handleSubmit(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!editing) {
        resolve(true); // close modal
        return;
      }
      try {
        if (data.username) data.username = data.username.trim();
        dispatch(
          patchUserAsync({
            data: {
              ...data,
              roles: userIsAdmin ? data.roles : undefined,
            },
            file: file,
            user: user,
          }),
        ).then((response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              // Note: patchUserAsync automatically refreshes the auth user state
              setEditing(false); // we can stop editing
              setActiveTab(profileTabs.profile);
              resolve(false); // keep modal open
              break;
            case 409:
              doFormMsg(
                {
                  message: intl.formatMessage({
                    id: 'USERS.SIGNUP.EMAIL_IN_USE',
                  }),
                  success: false,
                },
                setFormMsg,
              );
              resolve(false); // keep modal open
              break;
            case 500:
            default:
              doFormMsg(
                {
                  message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
                  success: false,
                },
                setFormMsg,
              );
              reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
              break;
          }
        });
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }));
      }
    });
  }

  function handleCancel(): boolean {
    setActiveTab(profileTabs.profile);
    setEditing(false);
    setData({ ...(author || user) });
    setPreviewSrc((author || user)?.avatar?.uri || null);
    // Reset URL hash to profile tab
    // Note: don't do this, it messes up when loading the dialogue
    // navigate('#profile', { replace: true });
    return false; // do not close modal yet
  }

  function handleClose(): boolean {
    return !editing;
  }

  const onResetPwd = () => {
    try {
      dispatch(userForgotAsync(data)).then((response: { payload: any }) => {
        switch (response.payload?.response.status) {
          case 200:
          case 204:
            doFormMsg(
              {
                message: intl.formatMessage({ id: 'USERS.FORGOT.SUCCESS' }),
                success: true,
              },
              setFormMsg,
            );
            break;
          default:
            break;
        }
      });
    } catch (err) {
      doFormMsg(
        {
          message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
          success: false,
        },
        setFormMsg,
      );
      console.log('Reset password request failed for unclear reason.', err);
    }
  };

  const avatar = <UserAvatar user={author || user} large />;
  return (
    <ModalDialog
      className="user_form"
      title={(author || user).username}
      okBtn={{
        label: editing ? (
          <FormattedMessage id="X.SAVE" />
        ) : (
          <FormattedMessage id="X.OK" />
        ),
        onClick: !author ? handleSubmit : undefined,
      }}
      cancelBtn={{
        onClick: editing ? handleCancel : undefined,
        hidden: !!author || !editing,
      }}
      editBtn={{
        label: <FormattedMessage id="X.EDIT" />,
        onClick: !author ? () => setEditing(true) : undefined,
        absent: !!author || editing,
      }}
      onClose={handleClose}
      enableEscape
      editing={editing}
      formMsg={formMsg}
      width="medium"
      height={editing ? 600 : undefined}
    >
      <Row>
        <Form.Group as={Col} controlId="fileDrop" sm={3} md={3} lg={3}>
          <FileUpload
            types={imageFileTypes}
            currentSrc={data.avatar?.uri || null}
            previewSrc={previewSrc}
            size={100}
            defaultImg={<UserAvatar user={author || user} large />}
            setPreviewSrc={setPreviewSrc}
            showTrigger={true}
            showMsg={(m) => doFormMsg(m, setFormMsg)}
            setFile={setFile}
            onDelete={() => {
              // set the id to null to indicate the avatar needs to be deleted
              data.avatar = null;
            }}
            confirmationText={
              <FormattedMessage id="USERS.PROFILE.DELETE_AVATAR_CNF" />
            }
            disabled={!editing}
            style={{ borderColor: color }}
          />
        </Form.Group>
        <Col sm={9} md={9} lg={9} className="col">
          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => {
              const newTab = (k as profileTabs) || profileTabs.profile;
              setActiveTab(newTab);
            }}
          >
            {author || !editing ? null : (
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey={profileTabs.profile}>
                    <FormattedMessage id="USERS.PROFILE.TABS.PROFILE" />
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={profileTabs.login}>
                    <FormattedMessage id="USERS.PROFILE.TABS.LOGIN" />
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            )}
            <Tab.Content>
              <Tab.Pane eventKey={profileTabs.profile}>
                <Row>
                  <Form.Group as={Row} controlId="Name">
                    <Col sm={12} md={12} lg={12}>
                      <Form.Control
                        className="field title"
                        disabled={!editing}
                        type="text"
                        name="username"
                        value={data.username || ''}
                        onChange={handleChangeData}
                        placeholder={intl.formatMessage({
                          id: 'USERS.PROFILE.NAME_PLCH',
                        })}
                        required
                        pattern={
                          data.username !== (author || user).username
                            ? getPattern('username')
                            : undefined
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        <div className="feedback">
                          <FormattedMessage id="USERS.SIGNUP.NAME_FDBCK" />
                        </div>
                      </Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} controlId="About">
                    <Col sm={12} md={12} lg={12}>
                      <Form.Control
                        className="field"
                        disabled={!editing}
                        type="text"
                        name="about"
                        as="textarea"
                        rows={5}
                        value={data.about ?? ''}
                        onChange={handleChangeData}
                        placeholder={
                          editing
                            ? intl.formatMessage({
                                id: 'USERS.PROFILE.ABOUT_PLCH',
                              })
                            : undefined
                        }
                        title={
                          editing
                            ? intl.formatMessage({
                                id: 'USERS.PROFILE.ABOUT_INSTR',
                              })
                            : undefined
                        }
                      />
                    </Col>
                  </Form.Group>
                </Row>
              </Tab.Pane>
              {author || !editing ? null : (
                <>
                  {/* Tab: Login data */}
                  <Tab.Pane eventKey={profileTabs.login}>
                    <Form.Group as={Row} controlId="Email">
                      <Col sm={12} md={12} lg={12}>
                        <Form.Control
                          className="field"
                          disabled={!editing}
                          type="email"
                          name="email"
                          value={data.email || ''}
                          onChange={handleChangeData}
                          placeholder={intl.formatMessage({
                            id: 'USERS.PROFILE.EMAIL_PLCH',
                          })}
                          required
                          pattern={getPattern('email')}
                          title={intl.formatMessage({
                            id: 'USERS.PROFILE.EMAIL_INSTR',
                          })}
                        />
                        <Form.Text className="text-muted instruction">
                          <FormattedMessage id="USERS.SIGNUP.EMAIL_INSTR" />
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          <div className="feedback">
                            <FormattedMessage id="USERS.LOGIN.EMAIL_FDBCK" />
                          </div>
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted instruction">
                          <FormattedMessage id="USERS.PROFILE.RESETPWD_INSTR" />
                          <br />
                          <Link onClick={onResetPwd} to={''}>
                            <FormattedMessage id="USERS.PROFILE.RESETPWD" />
                          </Link>
                        </Form.Text>
                      </Col>
                    </Form.Group>
                  </Tab.Pane>
                </>
              )}
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>
    </ModalDialog>
  );
}
